import lodash, { isEmpty } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import Container from '../../../1stlog-ui/src/components/Container'
import { usePrevious } from '../../../1stlog-ui/src/hooks'
import { usePrinters } from '../../../shipmetrics-shared/components/Printers'
import OrderEntry from '../../../shipmetrics-shared/containers/OrderEntry'
import { OrderEntryContextProvider } from '../../../shipmetrics-shared/containers/OrderEntry/OrderEntryContext'
import {
  TOrderEntryFormValues,
  TOrderEntrySettings,
} from '../../../shipmetrics-shared/containers/OrderEntry/types'
import {
  fixInitialValues,
  fixOrderEntrySettings,
  getInitialValuesFromSettings,
  mergeAdditionalValues,
} from '../../../shipmetrics-shared/containers/OrderEntry/utils'
import getUrlParamValues from '../../../shipmetrics-shared/containers/OrderEntry/utils/getUrlParamValues'
import { useCustomer } from '../../components/Customer'
import useApp from '../App/useApp'
import useTemplateValues from '../Templates/useTemplateValues'
import providers from './providers'
import { requestSubmit } from './requests'
import Success from './Success'

const Wrapper = styled.div``

type TProps = {
  match: object
}

const Order = (props: TProps): React.ReactElement => {
  const { match } = props

  const { scrollToTop } = useApp()

  const { templateValues, setTemplateValues } = useTemplateValues()
  useEffect(() => {
    return () => {
      setTemplateValues(null)
    }
  }, [])

  const externalInitialValues = useMemo<object | null>(() => null, []) // can be fed from outside (previous orders, templates)

  const [form, setForm] = useState({})
  const [initialValues, setInitialValues] = useState<TOrderEntryFormValues>()

  const { currentCustomer, presets } = useCustomer()

  const { currentPrinter, printers } = usePrinters()

  const presetId = lodash.get(match, 'params.presetId')

  const preset = useMemo(
    () =>
      lodash.find(presets, (p) => String(p.id) === String(presetId)) ||
      lodash.first(presets),
    [presets, presetId],
  )

  const depots = useMemo(() => [], []) // get depot array

  const settings: TOrderEntrySettings = useMemo(
    () =>
      fixOrderEntrySettings({
        id: 'AuftragOrderEntry',
        hazmatUrl: 'api/hazmat/search',
        barcodeUrl: 'api/validate',
        reusableBoxUrl: 'api/validateReusableBox',
        addressbookUrl: 'api/addressbook',
        customsUrl: 'api/customs',
        providers: currentCustomer
          ? providers
          : lodash.filter(providers, (p) => p.id !== 'standard'),
        currentCustomer,
        preset,
        // setPreset,
        // DebugControls,
        requestSubmit,
        initialValues: initialValues as TOrderEntryFormValues,
        currentPrinter:
          lodash.get(currentPrinter, 'id') > 0 ? currentPrinter : undefined,
        depots,
        // cancelButton,
        isInternal: false,
        scrollToTop,
      }),
    [currentCustomer, preset, initialValues, currentPrinter, depots],
  )

  const setInitialOrderEntryValues = (
    extraValuesArray: TOrderEntryFormValues[] = [],
    { preset, currentCustomer } = {
      preset: null,
      currentCustomer: null,
    },
  ): void => {
    let values = getInitialValuesFromSettings(settings)
    // console.log('values from settings', values)
    lodash.forEach(extraValuesArray, (extraValues) => {
      values = mergeAdditionalValues(
        values,
        // getValuesFromExternal(extraValues as TOrderEntryPayload),
        extraValues,
      )
    })
    // console.log('values after adding extra values', values)
    values = fixInitialValues(values, { preset, currentCustomer })
    // console.log('values after fixing', values)
    setInitialValues(values)
  }

  // build form values from settings and a possible array of external values
  useEffect(() => {
    let urlParamValues: object | null = null
    try {
      // const demoValues = `?initial_values={"deliveryAddress"%3A{"name1"%3A"Traeume in Lack GmbH%2C Unfall %26 Lacki"%2C"street"%3A"Laugkfeld 12"%2C"zip"%3A"01968"%2C"city"%3A"Senftenberg"%2C"country"%3A"DE"}%2C"slaType"%3A"TALL"%2C"colis"%3A[{"weight"%3A3%2C"height"%3A12%2C"width"%3A12%2C"length"%3A30%2C"coliReferenceText"%3A"25482"}]}`
      urlParamValues = getUrlParamValues(window.location.search)
    } catch (error) {
      toast.error(
        i18n.t(
          'failed_to_load_order_url_params',
          'URL-Parameter konnten nicht geladen werden. Bitte prüfen Sie den verwendeten Link.',
        ),
      )
    }
    const extraValues = [externalInitialValues]

    // only use urlParamValues if templateValues are not set
    // console.log('templateValues', templateValues)
    if (isEmpty(templateValues)) {
      extraValues.push(urlParamValues)
    } else {
      extraValues.push(templateValues)
    }

    setInitialOrderEntryValues(
      extraValues.filter(Boolean) as TOrderEntryFormValues[],
      {
        preset,
        currentCustomer,
      },
    )
  }, [preset, currentCustomer, externalInitialValues, templateValues])

  // re-mount form when initialValues change
  const [formKey, setFormKey] = useState<string>()
  const shuffleFormId = (): void => setFormKey(uuid())
  const previousInitialValues = usePrevious(initialValues)
  useEffect(() => {
    if (
      !initialValues ||
      (!previousInitialValues &&
        lodash.isEqual(previousInitialValues, initialValues))
    ) {
      return
    }
    shuffleFormId()
  }, [initialValues])

  // successfulOrder contains the last sucessfully submitted order payload/label info
  const [successfulOrder, setSuccessfulOrder] = useState<{
    order: any
    labelToken: string
  }>()

  // console.log('successfulOrder', successfulOrder)

  // called when the OrderEntry form submitted without errors and the order was created
  const onSuccess = (payload): void => {
    setSuccessfulOrder(payload)
  }

  const closeSuccess = (): void => setSuccessfulOrder(undefined)

  // setting/clearing form values from previous orders
  const onClearForm = (): void => setInitialOrderEntryValues()

  return (
    <>
      {initialValues && formKey ? (
        <Container className="mt-3 mb-2">
          <Wrapper>
            {successfulOrder ? (
              <Success
                order={successfulOrder.order}
                labelToken={successfulOrder.labelToken}
                onClose={closeSuccess}
              />
            ) : (
              <OrderEntryContextProvider value={settings} key={formKey}>
                <OrderEntry onSuccess={onSuccess} setForm={setForm} />
              </OrderEntryContextProvider>
            )}
          </Wrapper>
        </Container>
      ) : (
        <></>
        // <LoaderOverlay isLoading fullscreen />
      )}
    </>
  )
}

export default Order
